/* This is a copy of the modal, 
with some changes, so as not to affect 
the general component. */
import { ReactNode, useState } from 'react'

import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export interface ModalWizardProps {
  title?: string
  subtitle?: string
  footer?: ReactNode
  isOpen: boolean
  onClose: (_cancelled: boolean) => void
  size?: 'xxs' | 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'
  modalClassName?: string
  confirmOnClose?: boolean
  showDefaultDescription?: boolean
}

const sizesClassName = {
  ['xxs']: 'sm:max-w-xs md:max-w-sm lg:max-w-md',
  ['xs']: 'sm:max-w-md md:max-w-lg lg:max-w-xl',
  ['s']: 'sm:max-w-lg md:max-w-xl lg:max-w-2xl',
  ['m']: 'sm:max-w-xl md:max-w-2xl lg:max-w-3xl',
  ['l']: 'sm:max-w-2xl md:max-w-3xl lg:max-w-4xl',
  ['xl']: 'sm:max-w-3xl md:max-w-4xl lg:max-w-5xl',
  ['xxl']: 'sm:max-w-4xl md:max-w-5xl lg:max-w-6xl',
}

export const ModalWizard = ({
  title,
  subtitle,
  footer,
  isOpen,
  onClose,
  size = 'l',
  modalClassName,
  children,
  confirmOnClose = false,
  showDefaultDescription = true,
}: ModalWizardProps & { children: ReactNode }) => {
  const [showConfirm, setShowConfirm] = useState(false)

  const handleRequestClose = () => {
    if (confirmOnClose) {
      setShowConfirm(true)
    } else {
      onClose(false)
    }
  }

  return (
    <>
      <Dialog
        onOpenChange={(state) => {
          if (!state) handleRequestClose()
        }}
        open={isOpen}
      >
        <DialogContent
          aria-describedby={
            subtitle || showDefaultDescription ? 'modal-description' : undefined
          }
          className={cn(
            'flex h-fit max-h-[90dvh] flex-col gap-0 overflow-hidden',
            sizesClassName[size],
            modalClassName
          )}
        >
          <DialogHeader className="flex-shrink-0 pb-4">
            {title && (
              <DialogTitle className="text-xl md:text-2xl">{title}</DialogTitle>
            )}
            {(subtitle || showDefaultDescription) && (
              <DialogDescription
                className="text-sm text-muted-foreground"
                id="modal-description"
              >
                {subtitle ||
                  (title
                    ? `Configuración para ${title}`
                    : 'Configuración del modal') ||
                  'Modal de configuración'}
              </DialogDescription>
            )}
          </DialogHeader>
          <div
            className={
              footer ? 'mb-2 flex min-h-0 flex-1 flex-col overflow-hidden' : ''
            }
          >
            {children}
          </div>
          {footer && (
            <DialogFooter
              className={cn(
                'flex-shrink-0 items-center gap-3 border-t px-4 pb-0 pt-3',
                '-mb-3 -mt-2 sm:flex-row sm:justify-between sm:px-6'
              )}
            >
              {footer}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {/* Diálogo de confirmación al cerrar */}
      <AlertDialog open={showConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar el proceso?</AlertDialogTitle>
            <AlertDialogDescription>
              Si cancela ahora, perderá todos los datos ingresados. ¿Está seguro
              de que desea cancelar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowConfirm(false)}>
              Continuar editando
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowConfirm(false)
                onClose(false)
              }}
            >
              Sí, cancelar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
